import { throttle } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Thumbnails } from './Thumbnails';

declare global {
  interface HTMLDivElement {
    isScrolling?: boolean;
  }
}

export default function MobileTrimBar({
  mode,
  value,
  trimStart,
  trimEnd,
  maxValue,
  thumbnails,
  onValueChange,
  onTrimChange,
}: {
  mode: 'start' | 'end';
  value?: number;
  trimStart: number;
  trimEnd: number;
  maxValue: number;
  thumbnails?: string[];
  onValueChange?: (value: number) => void;
  onTrimChange: (start: number, end: number) => void;
}) {
  const [currentScroll, setCurrentScroll] = useState(0);

  const width = Math.max(maxValue * 100, 17 * 50);
  const viewRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const valueToScrollPosition = (value: number, view: HTMLDivElement) => {
    const maxScroll = viewScrollWidth(view) - view.clientWidth;
    return (value / maxValue) * maxScroll;
  };

  const viewScrollWidth = useCallback((view: HTMLDivElement | null) => {
    if (!view) return 0;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const scrollWidth = view.scrollWidth;
    const clientWidth = view.clientWidth;

    // We add 50% of the clientWidth to account for 50% padding. That's because safari calculates scrollWidth differently.
    return isSafari ? scrollWidth + clientWidth * 0.5 : scrollWidth;
  }, []);

  const scrollPositionToValue = (
    scrollPosition: number,
    view: HTMLDivElement,
  ) => {
    const maxScroll = viewScrollWidth(view) - view.clientWidth;
    return (scrollPosition / maxScroll) * maxValue;
  };

  // Modify the effect to only run on mount and mode changes
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const maxScroll = viewScrollWidth(view) - view.clientWidth;
    let targetValue;

    if (mode === 'start') {
      targetValue = trimStart;
    } else {
      targetValue = Math.max(trimEnd, trimStart);
    }

    // Convert the target value to scroll position
    const scrollPosition = (targetValue / maxValue) * maxScroll;
    view.scrollLeft = scrollPosition;
    setCurrentScroll(scrollPosition);
  }, [mode]); // Only depend on mode changes, not trimStart/trimEnd/maxValue

  // Add a separate effect to handle prop updates without forcing scroll position
  useEffect(() => {
    const view = viewRef.current;
    if (!view || !view.isScrolling) {
      // Only update if not actively scrolling
      setCurrentScroll(view?.scrollLeft ?? 0);
    }
  }, [trimStart, trimEnd, maxValue]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const handleScroll = throttle(() => {
      const currentScroll = view.scrollLeft;
      let newValue = scrollPositionToValue(currentScroll, view);

      // Apply constraints based on mode
      if (mode === 'start') {
        // Prevent scrolling past trimEnd - minGap
        const maxAllowedValue = trimEnd;
        if (newValue > maxAllowedValue) {
          newValue = maxAllowedValue;
          view.scrollLeft = valueToScrollPosition(newValue, view);
        }
        onTrimChange(newValue, trimEnd);

        // Show the frame that is dragged
        onValueChange?.(newValue - trimStart);
      } else {
        // End mode constraints
        const minAllowedValue = trimStart;
        const maxAllowedValue = maxValue;

        if (view.scrollLeft > 0) {
          if (newValue < minAllowedValue) {
            newValue = minAllowedValue;
            view.scrollLeft = valueToScrollPosition(newValue, view);
          } else if (newValue > maxAllowedValue) {
            newValue = maxAllowedValue;
            view.scrollLeft = valueToScrollPosition(newValue, view);
          }
          onTrimChange(trimStart, newValue);
          // Show the frame that is dragged
          onValueChange?.(newValue - trimStart);
        }
      }

      setCurrentScroll(view.scrollLeft);
    }, 16); // throttle to ~60fps

    view.addEventListener('scroll', handleScroll);
    return () => {
      view.removeEventListener('scroll', handleScroll);
      handleScroll.cancel(); // Clean up throttled function
    };
  }, [mode, maxValue, trimEnd, trimStart, onTrimChange]);

  return (
    <>
      <div className="relative">
        <div className="relative z-0  overflow-x-hidden">
          <div
            ref={viewRef}
            className="no-scrollbar w-full overflow-x-scroll px-[50%]"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div
              ref={trackRef}
              className="relative h-[38px] rounded-lg"
              style={{ width }}
            >
              <Thumbnails thumbnails={thumbnails} />

              {mode === 'start' ? (
                <div
                  className="bg-gray-90/60 pointer-events-none absolute right-0 top-0 h-full rounded-r-lg"
                  style={{
                    width: `${((maxValue - trimEnd) / maxValue) * 100}%`,
                  }}
                />
              ) : (
                <div
                  className="bg-gray-90/60 pointer-events-none absolute left-0 top-0 h-full rounded-l-lg"
                  style={{
                    width: `${(trimStart / maxValue) * 100}%`,
                  }}
                />
              )}
            </div>
          </div>
          {mode === 'start' ? (
            <div
              className="bg-gray-90/60 pointer-events-none absolute right-1/2 top-0 h-full rounded-l-lg"
              style={{
                width: viewRef.current
                  ? `${(currentScroll / viewRef.current.clientWidth) * 100}%`
                  : 0,
              }}
            />
          ) : (
            <div
              className="bg-gray-90/60 pointer-events-none absolute left-1/2 top-0 h-full rounded-r-lg"
              style={{
                width: viewRef.current
                  ? `${
                      ((viewScrollWidth(viewRef.current) -
                        viewRef.current.clientWidth -
                        currentScroll) /
                        viewRef.current.clientWidth) *
                      100
                    }%`
                  : 0,
              }}
            />
          )}
        </div>
        <div className="outline-gray-90/30 absolute bottom-[-2px] left-[calc(50%-2px)] top-[-2px] z-10 w-1 rounded-full bg-white outline-2" />
      </div>
    </>
  );
}
